import React, { Fragment } from 'react';
import pluralize from 'pluralize';
import EmployeeListsOverview from './employee-lists-overview';
import SelectUsers from '../../../containers/users/components/select-users-step';
import Modal from 'react-modal';
import {
    Heading3,
    TextDefault,
    FlexDiv,
    ThemedButton,
    LightTooltip,
} from '@awarego-ui/components';
import WizardStepHeader from './WizardStepHeader';
import { Link } from 'react-router-dom';

function getRecommendationFilterCopy(item) {
    if (!item.hasRecommendationUserFilter) return null;

    const threshold = item.recommendationScoreThreshold;
    const eligibleCount = item.recommendationEligibleEmployeesCount;

    return {
        subtitle: `Lists are pre-selected from the assessment. Employees receive only the modules for threat areas where their score is below ${threshold}, plus the follow-up test once if needed. Employees who did not complete the assessment receive all modules. Sync users is inactive.`,
        summary:
            eligibleCount != null
                ? `${eligibleCount} ${pluralize(
                      'employee',
                      eligibleCount
                  )} will receive this training`
                : `Employees below score ${threshold} in any threat area, or not completed, will receive training`,
        tooltip:
            'Employee lists cannot be changed for assessment-based recommendations. Each employee receives only modules for areas they scored below the threshold (shared modules and the follow-up test are assigned once).',
        listNote:
            eligibleCount != null
                ? `${eligibleCount} of ${item.selectedUserCount} ${pluralize(
                      'employee',
                      item.selectedUserCount
                  )} in list will receive training (per-area score filter)`
                : `Only employees below score ${threshold} in a threat area, or not completed, will receive training`,
    };
}

function SelectEmployees({
    itemString,
    item,
    lists,
    toggleUserListSelected,
    ongoing,
    addEmployeeModal,
    showAddEmployeeModal,
    hideAddEmployeeModal,
    recipientsModal,
    showRecipientsModal,
    hideRecipientsModal,
    error,
    loadingUsers,
    requestUsersForList,
    selectedLists,
    selectedUserCount,
    disabled,
    createRecipientsList,
    currentCompany,
    redirectToUpgrade,
}) {
    const filterCopy = getRecommendationFilterCopy(item);
    const listsLocked = Boolean(filterCopy);
    const defaultSummary = `${item.selectedUserCount} ${pluralize(
        'employee',
        item.selectedUserCount
    )} selected`;

    const selectEmployeesButton = (text, onClick) => {
        const button = (
            <ThemedButton
                onClick={onClick}
                mode="secondary"
                text={text}
                disabled={listsLocked}
            />
        );

        if (listsLocked) {
            return (
                <LightTooltip title={filterCopy.tooltip} placement="top">
                    <span>{button}</span>
                </LightTooltip>
            );
        }

        return button;
    };

    return (
        <Fragment>
            <WizardStepHeader
                title={'Select employees or create a new list'}
                subtitle={filterCopy?.subtitle}
                summary={filterCopy?.summary || defaultSummary}
                buttonText={
                    item.selectedUserCount > 0 || listsLocked
                        ? 'Select employees'
                        : null
                }
                onButtonClick={showAddEmployeeModal}
                buttonDisabled={listsLocked}
                buttonTooltip={filterCopy?.tooltip}
            />
            <div className="table">
                {lists.length > 0 ? (
                    item.selectedUserCount > 0 ? (
                        <>
                            {filterCopy?.listNote && (
                                <TextDefault lighter mb={16}>
                                    {filterCopy.listNote}
                                </TextDefault>
                            )}
                            <EmployeeListsOverview
                                lists={item.userListsSelected}
                                onRemoveList={toggleUserListSelected}
                                item={item}
                                ongoing={ongoing}
                                itemString={itemString}
                                currentCompany={currentCompany}
                                disableRemove={listsLocked}
                            />
                        </>
                    ) : (
                        <FlexDiv pt={48} alignCenter column justifyCenter>
                            <FlexDiv mb={16}>
                                <img
                                    src="/static/img/employee-list-empty.svg"
                                    alt="No employees selected yet"
                                />
                            </FlexDiv>
                            <Heading3 mt={16} mb={8} displayBlock>
                                No employees selected yet
                            </Heading3>
                            <TextDefault lighter>
                                Choose which employee lists will receive this{' '}
                                {itemString}
                            </TextDefault>
                            <FlexDiv mt={24} mb={32}>
                                {selectEmployeesButton(
                                    'Select employees',
                                    showAddEmployeeModal
                                )}
                            </FlexDiv>
                        </FlexDiv>
                    )
                ) : (
                    <FlexDiv alignCenter column justifyCenter gap={16} pt={48}>
                        <img
                            src="/static/img/employee-list-empty.svg"
                            alt="a list of employee departments"
                        />
                        <Heading3 my={16}>No employee lists yet</Heading3>
                        {listsLocked ? (
                            <LightTooltip
                                title={filterCopy.tooltip}
                                placement="top"
                            >
                                <span>
                                    <ThemedButton
                                        mode="secondary"
                                        text="Create new list"
                                        disabled
                                    />
                                </span>
                            </LightTooltip>
                        ) : (
                            <ThemedButton
                                onClick={showRecipientsModal}
                                mode="secondary"
                                text="Create new list"
                            />
                        )}
                        <TextDefault mt={8}>
                            Or{' '}
                            <TextDefault bold themed>
                                <Link to="/settings/integrations">
                                    set up integrations
                                </Link>
                            </TextDefault>{' '}
                            to sync your employees automatically
                        </TextDefault>
                    </FlexDiv>
                )}
            </div>
            <Modal
                ariaHideApp={false}
                isOpen={addEmployeeModal}
                onRequestClose={hideAddEmployeeModal}
                className="Modal schedule"
            >
                <SelectUsers
                    lists={lists}
                    showRecipientsModal={showRecipientsModal}
                    recipientsModal={recipientsModal}
                    hideRecipientsModal={hideRecipientsModal}
                    createRecipientsList={createRecipientsList}
                    item={item}
                    error={error}
                    loadingUsers={loadingUsers}
                    requestUsersForList={requestUsersForList}
                    selectedLists={selectedLists}
                    selectedUserCount={selectedUserCount}
                    onUserListSelected={toggleUserListSelected}
                    hideAddEmployeeModal={hideAddEmployeeModal}
                    disabled={disabled || listsLocked}
                    ongoing={ongoing}
                    currentCompany={currentCompany}
                    redirectToUpgrade={redirectToUpgrade}
                />
            </Modal>
        </Fragment>
    );
}

export default SelectEmployees;
